import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { UserCard } from "@/components/user-card/UserCard.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { EmployeePageComponentProps } from "@/pages/employees/employee/components/employee-page-components.types.ts";
import { EmployeePositionEditModal } from "@/pages/employees/employee/widgets/EmployeePositionEditModal.tsx";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { useEmployees } from "@/queries/employees/use-employees.query.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  ApiEmployeeFilter,
  getActiveEmployeeLifecycleStatuses,
} from "@/types/employees/employee.types.ts";
import { ApiPermission, EmployeeFieldPermission } from "@/types/role.types.ts";
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  PaginationBlock,
  Skeleton,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const defaultVisibleDepartmentEmployeesCount = 8;

export function EmployeePageOverviewDepartmentEmployees({ employee }: EmployeePageComponentProps) {
  const { t } = useTranslation();
  const { canIEdit, canEditField, canCurrentEmployeeEditField } = usePermissions();
  const { renderEmployeeName } = useFormatter();
  const { openModal } = useModals();
  const navigate = useNavigate();
  const [isShowAllDepartmentEmployees, setIsShowAllDepartmentEmployees] = useState(false);

  const department = employee.employee_position?.department;

  const [filter, setFilter] = useState<ApiEmployeeFilter>({
    page_number: 1,
    department_ids: department?.id ? [department.id] : undefined,
    exclude_ids: [employee.id],
    employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
  });

  const [syncedDepartmentId, setSyncedDepartmentId] = useState(department?.id);
  const [syncedEmployeeId, setSyncedEmployeeId] = useState(employee.id);

  if (department?.id !== syncedDepartmentId || employee.id !== syncedEmployeeId) {
    setSyncedDepartmentId(department?.id);
    setSyncedEmployeeId(employee.id);
    setFilter((prevFilter) => ({
      ...prevFilter,
      department_ids: department?.id ? [department.id] : undefined,
      exclude_ids: [employee.id],
      page_number: 1,
      employee_lifecycle_statuses: getActiveEmployeeLifecycleStatuses(),
    }));
    setIsShowAllDepartmentEmployees(false);
  }

  const { data: departmentEmployees, isLoading } = useEmployees(filter, !!department?.id);

  const coworkersCount = departmentEmployees?.metadata.total_items ?? 0;
  const hasDepartment = !!department?.id;
  const visibleDepartmentEmployees = isShowAllDepartmentEmployees
    ? departmentEmployees?.items
    : departmentEmployees?.items?.slice(0, defaultVisibleDepartmentEmployeesCount);
  const remainingDepartmentEmployeesCount = Math.max(
    coworkersCount - defaultVisibleDepartmentEmployeesCount,
    0
  );
  const isEmpty = !isLoading && (!hasDepartment || coworkersCount === 0);
  const emptyTitle = hasDepartment
    ? t("core:employee_overview_no_department_employees")
    : t("core:employee_overview_no_department_assigned");
  const emptyDescription = hasDepartment
    ? t("core:employee_overview_no_department_employees_description")
    : t("core:employee_overview_no_department_assigned_description");
  const emptyButtonText = hasDepartment
    ? t("core:employee_overview_no_department_employees_button")
    : t("core:employee_overview_no_department_assigned_button");
  const shouldShowMoreButton =
    !isLoading &&
    !isEmpty &&
    !isShowAllDepartmentEmployees &&
    remainingDepartmentEmployeesCount > 0;
  const canViewDepartmentChart = canCurrentEmployeeEditField(
    EmployeeFieldPermission.see_departments_structure
  );
  const canAddEmployee = canIEdit(ApiPermission.add_employees);
  const canEditPosition = canEditField(employee, EmployeeFieldPermission.manage_employee_position);
  const canShowEmptyStateDetails = hasDepartment ? canAddEmployee : canEditPosition;
  const departmentTitle = department?.title ?? t("core:department");
  const titleCaption = isLoading
    ? "..."
    : isEmpty
      ? undefined
      : t("plurals:employees_count", { count: coworkersCount });

  return (
    <div className={"flex flex-row gap-9 py-0"}>
      <div className={"w-full overflow-hidden"}>
        <Stack direction={"vertical"} gap={"md"}>
          {canViewDepartmentChart ? (
            <Stack direction={"horizontal"} justify={"between"} items={"center"} gap={"md"}>
              <Title size={"lg"} header={departmentTitle} caption={titleCaption} />
              <DropdownMenu
                trigger={
                  <Button
                    variant={"secondary"}
                    size={"md"}
                    rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
                  >
                    {t("common:actions")}
                  </Button>
                }
              >
                <DropdownMenuItem
                  to={
                    department?.id
                      ? `/departments/chart?department_id=${department.id}`
                      : "/departments/chart"
                  }
                  target="_blank"
                  leftIcon={<FontAwesomeIcon icon="fa-light fa-sitemap" />}
                  title={t("core:org_chart_departments")}
                />
              </DropdownMenu>
            </Stack>
          ) : (
            <Title size={"lg"} header={departmentTitle} caption={titleCaption} />
          )}

          {isLoading && (
            <Stack direction={"horizontal"} gap={"md"}>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
              <Stack className={"w-41"} gap={"sm"}>
                <Skeleton className={"w-41 h-41 rounded-lg"} />
                <Skeleton className={"w-40 h-4"} />
                <Skeleton className={"w-40 h-4"} />
              </Stack>
            </Stack>
          )}

          {isEmpty ? (
            <EmptyStateBase
              variant="primary"
              title={emptyTitle}
              description={canShowEmptyStateDetails ? emptyDescription : undefined}
              emoji={":office:"}
              primaryButton={
                canShowEmptyStateDetails && !hasDepartment ? (
                  <Button
                    onClick={() => {
                      openModal(EmployeePositionEditModal, {
                        employee,
                        employeePosition: undefined,
                      });
                    }}
                  >
                    {emptyButtonText}
                  </Button>
                ) : canShowEmptyStateDetails && hasDepartment ? (
                  <Button
                    onClick={() => {
                      navigate("/employees");
                    }}
                  >
                    {emptyButtonText}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <>
              <Box
                className={"grid gap-(--gap-md)"}
                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(10rem, 1fr))" }}
              >
                {visibleDepartmentEmployees?.map((employeeItem) => (
                  <UserCard
                    onClick={() => {
                      openModal(EmployeeViewModal, {
                        employeeId: employeeItem.id,
                      });
                    }}
                    to={`/employees/${employeeItem.id}`}
                    key={`department-card-${employeeItem.id}`}
                    size={"md"}
                    className={"w-full"}
                    avatarElement={<EmployeeAvatar employee={employeeItem} size={"2xl"} />}
                    title={renderEmployeeName(employeeItem)}
                    caption={getEmployeePositionTitle(employeeItem)}
                  />
                ))}
              </Box>

              {shouldShowMoreButton && (
                <Stack className="pt-(--gap-md)" direction="horizontal">
                  <Button variant="secondary" onClick={() => setIsShowAllDepartmentEmployees(true)}>
                    {t("common:show_more_template", { count: remainingDepartmentEmployeesCount })}
                  </Button>
                </Stack>
              )}
            </>
          )}

          {!isLoading &&
            !isEmpty &&
            isShowAllDepartmentEmployees &&
            departmentEmployees &&
            departmentEmployees.metadata.total_pages > 1 && (
              <PaginationBlock
                metadata={departmentEmployees.metadata}
                onPageChange={(page) => {
                  setFilter((prevFilter) => ({ ...prevFilter, page_number: page }));
                }}
              />
            )}
        </Stack>
      </div>
    </div>
  );
}
